import { TOOLBAR_BUTTON_SX } from "@/controls/buttons/toolbarButton";
import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import { Box, IconButton, MenuItem, TextField, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import type { ForecastMode } from "../models/ForecastReport";

const FIRST_YEAR = 1901;
const LAST_YEAR = 2199;

interface YearPickerProps {
	label: string;
	year: number;
	minYear?: number;
	maxYear?: number;
	disabled: boolean;
	onChange: (year: number) => void;
}

const YearPicker = ({
	label,
	year,
	minYear = FIRST_YEAR,
	maxYear = LAST_YEAR,
	disabled,
	onChange,
}: YearPickerProps) => (
	<Box sx={{ width: 100 }}>
		<DatePicker
			label={label}
			views={["year"]}
			format="YYYY"
			value={moment(String(year), "YYYY")}
			minDate={moment(String(minYear), "YYYY")}
			maxDate={moment(String(maxYear), "YYYY")}
			disabled={disabled}
			onChange={(value) => {
				// Typing passes through 0002, 0020, 0202 before the full year
				const typed = value?.isValid() ? value.year() : null;
				if (typed !== null && typed >= FIRST_YEAR && typed <= LAST_YEAR)
					onChange(typed);
			}}
			slotProps={{
				textField: {
					size: "small",
					fullWidth: true,
					sx: { "& input": { textAlign: "right" } },
				},
			}}
		/>
	</Box>
);

// The field height and dense margin, like every other toolbar button.
const navButtonSx = {
	...TOOLBAR_BUTTON_SX,
	bgcolor: "primary.main",
	color: "white",
	width: 36,
	height: 36,
	"&:hover": { bgcolor: "primary.dark" },
	"&.Mui-disabled": {
		bgcolor: "action.disabledBackground",
		color: "action.disabled",
	},
};

interface Props {
	startYear: number;
	endYear: number;
	mode: ForecastMode;
	onStartYearChange: (y: number) => void;
	onEndYearChange: (y: number) => void;
	onModeChange: (m: ForecastMode) => void;
	isLoading: boolean;
	onReset: () => void;
	canReset: boolean;
}

export const ForecastReportToolbar = ({
	startYear,
	endYear,
	mode,
	onStartYearChange,
	onEndYearChange,
	onModeChange,
	isLoading,
	onReset,
	canReset,
}: Props) => {
	const handleStartChange = (y: number) => {
		const next = y > endYear ? endYear : y;
		onStartYearChange(next);
	};

	const handleEndChange = (y: number) => {
		const next = y < startYear ? startYear : y;
		onEndYearChange(next);
	};

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				px: 1.5,
				py: 0.75,
				flexWrap: "wrap",
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
				<Tooltip title="Previous year">
					<span>
						<IconButton
							onClick={() => handleStartChange(startYear - 1)}
							disabled={isLoading}
							size="small"
							sx={navButtonSx}
						>
							<ChevronLeftIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
				<YearPicker
					label="Start"
					year={startYear}
					maxYear={endYear}
					disabled={isLoading}
					onChange={handleStartChange}
				/>
				<Tooltip title="Next year">
					<span>
						<IconButton
							onClick={() => handleStartChange(startYear + 1)}
							disabled={isLoading}
							size="small"
							sx={navButtonSx}
						>
							<ChevronRightIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			</Box>

			<Box sx={{ width: 8 }} />

			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
				<Tooltip title="Previous year">
					<span>
						<IconButton
							onClick={() => handleEndChange(endYear - 1)}
							disabled={isLoading}
							size="small"
							sx={navButtonSx}
						>
							<ChevronLeftIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
				<YearPicker
					label="End"
					year={endYear}
					minYear={startYear}
					disabled={isLoading}
					onChange={handleEndChange}
				/>
				<Tooltip title="Next year">
					<span>
						<IconButton
							onClick={() => handleEndChange(endYear + 1)}
							disabled={isLoading}
							size="small"
							sx={navButtonSx}
						>
							<ChevronRightIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			</Box>

			<Box sx={{ width: 16 }} />

			<Box sx={{ width: 140 }}>
				<TextField
					select
					label="Mode"
					value={mode}
					onChange={(e) => onModeChange(e.target.value as ForecastMode)}
					size="small"
					fullWidth
					disabled={isLoading}
					slotProps={{ inputLabel: { shrink: true } }}
				>
					<MenuItem value="Actual">Actual</MenuItem>
					<MenuItem value="Forecast">Forecast</MenuItem>
					<MenuItem value="Mixed">Mixed</MenuItem>
				</TextField>
			</Box>
			<ResetFiltersButton onClick={onReset} disabled={!canReset} />
		</Box>
	);
};
